/**
 * A small async confirmation dialog for dashboard actions (archive a shop,
 * delete an owner, deactivate a user). It runs the caller's async
 * `onConfirm`, shows a busy state and any failure inline, and guards
 * against a double submit. Destructive actions colour the button red (the
 * one place a CONTAINED red button is allowed -- owner ruling 2026-07-14);
 * everything else is contained primary blue. Ported from shop/ui's admin
 * ConfirmDialog.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { QuietButton } from './QuietButton.tsx';
import { actionError } from './actionError.ts';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  confirmColor?: 'primary' | 'error';
  onConfirm: () => Promise<void>;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  confirmColor = 'primary',
  onConfirm,
  onClose,
  onSuccess,
}: Readonly<ConfirmDialogProps>): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setBusy(false);
      setError('');
      submittingRef.current = false;
    }
  }, [open]);

  const confirm = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError('');
    try {
      await onConfirm();
      onSuccess?.();
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {body}
        {error && (
          <Alert role="alert" severity="error" sx={{ mt: 2, fontWeight: 700 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button color={confirmColor} onClick={() => void confirm()} disabled={busy}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
